import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  Plus,
  Edit,
  Trash2,
  Clock,
  User,
  Building2,
  AlertCircle,
  HelpCircle,
  X,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL;
const ACCESS_TOKEN = "accessToken";

function SDA_Log() {
  const [openGuide, setOpenGuide] = useState(false);

  const getToken = () =>
    localStorage.getItem(ACCESS_TOKEN) || sessionStorage.getItem(ACCESS_TOKEN);

  const fetchJSON = async (url) => {
    const token = getToken();

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    });

    const text = await res.text().catch(() => "");
    if (!res.ok) {
      throw new Error(
        `Request failed (${res.status}): ${text || res.statusText}`,
      );
    }

    try {
      return text ? JSON.parse(text) : null;
    } catch {
      return null;
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-sda-activities"],
    queryFn: async () => {
      const activitiesRes = await fetchJSON(
        `${API_URL}/api/sdaowner/activities`,
      );

      const raw = Array.isArray(activitiesRes)
        ? activitiesRes
        : (activitiesRes && activitiesRes.activities) || [];

      return raw.map((item) => ({
        ...item,
        ownerName:
          item.ownerName || item.actor || item.user_name || "Unknown user",
        accommodationTitle:
          item.accommodationTitle ||
          item.title ||
          item.accommodation_title ||
          "—",
        accommodationId:
          item.accommodationId || item.accommodation_id || item.id,
        timestamp: item.timestamp || item.created_at || item.createdAt,
        action: item.action || item.type || "updated",
      }));
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: (prev) => prev,
  });

  const activities = data || [];
  const loading = isLoading;
  const errorMessage = error ? error.message || "Unknown error" : null;

  // --- NEW: sort activities newest-first before grouping ---
  const sortedActivities = activities.slice().sort((a, b) => {
    const ta = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
    const tb = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
    return tb - ta; // newest first
  });

  // We'll group by ISO date (yyyy-mm-dd) so sorting remains reliable,
  // and also store a display label for each date.
  const groupedObj = sortedActivities.reduce(
    (acc, activity) => {
      const ts = activity.timestamp ? new Date(activity.timestamp) : new Date();
      // ISO date key for reliable sorting: "YYYY-MM-DD"
      const isoKey = ts.toISOString().slice(0, 10);
      // Human-readable label (same as original)
      const displayLabel = ts.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      if (!acc.groups[isoKey]) {
        acc.groups[isoKey] = [];
        acc.labels[isoKey] = displayLabel;
      }
      acc.groups[isoKey].push(activity);
      return acc;
    },
    { groups: {}, labels: {} },
  );

  // Prepare an ordered array of [displayLabel, activitiesArray] in newest-date-first order
  const groupedEntries = Object.keys(groupedObj.groups)
    .sort((a, b) => new Date(b) - new Date(a)) // iso date keys sort correctly
    .map((isoKey) => [groupedObj.labels[isoKey], groupedObj.groups[isoKey]]);

  function formatTimestamp(iso) {
    const dt = new Date(iso);
    const diff = Date.now() - dt.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days <= 7) return `${days}d ago`;

    return dt.toLocaleString(undefined, { month: "short", day: "numeric" });
  }

  function formatFullTimestamp(iso) {
    const dt = new Date(iso);
    return dt.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  function getActionText(action) {
    switch (action) {
      case "add":
        return "Added";
      case "edit":
        return "Edited";
      case "delete":
        return "Deleted";
      default:
        return "Updated";
    }
  }

  function getActionColor(action) {
    switch (action) {
      case "add":
        return "border-emerald-500 text-emerald-500";
      case "edit":
        return "border-sky-500 text-sky-500";
      case "delete":
        return "border-rose-500 text-rose-500";
      default:
        return "border-gray-400 text-gray-500";
    }
  }

  function getActionIcon(action) {
    const commonProps = { className: "h-4 w-4" };
    switch (action) {
      case "add":
        return <Plus {...commonProps} />;
      case "edit":
        return <Edit {...commonProps} />;
      case "delete":
        return <Trash2 {...commonProps} />;
      default:
        return <AlertCircle {...commonProps} />;
    }
  }

  return (
    <div>
      <Card>
        <CardHeader className="bg-gray-50/50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-gray-900">Activity History</CardTitle>

              <button
                type="button"
                onClick={() => setOpenGuide(true)}
                className="h-8 w-8 rounded-full border border-gray-200 hover:bg-gray-100 flex items-center justify-center"
                aria-label="Open SDA Log guide">
                <HelpCircle className="h-4 w-4 text-gray-600" />
              </button>
            </div>

            <Badge variant="outline" className="text-xs">
              {activities.length}{" "}
              {activities.length === 1 ? "activity" : "activities"}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">Loading activities…</div>
          ) : errorMessage ? (
            <div className="p-6 text-sm text-rose-600">
              Error: {errorMessage}
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <Clock className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-gray-900 mb-2">No Activity Yet</h3>
              <p className="text-gray-500 text-center max-w-md">
                When SDA Owners add, edit, or delete accommodations, their
                activities will appear here.
              </p>
            </div>
          ) : (
            <ScrollArea className="">
              <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
                {groupedEntries.map(([dateLabel, dateActivities]) => (
                  <div key={dateLabel} className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-gray-200" />
                      <p className="text-xs text-gray-500 uppercase tracking-wider px-2">
                        {dateLabel}
                      </p>
                      <div className="h-px flex-1 bg-gray-200" />
                    </div>

                    <div className="space-y-3">
                      {dateActivities.map((activity) => (
                        <div
                          key={activity.id}
                          className="relative pl-6 sm:pl-8 pb-4 border-l-2 border-gray-200 last:border-l-0 last:pb-0">
                          <div
                            className={`absolute left-[-9px] top-0 h-4 w-4 rounded-full border-2 ${getActionColor(
                              activity.action,
                            )} bg-white`}>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div
                                className={`h-2 w-2 rounded-full ${getActionColor(
                                  activity.action,
                                )}`}></div>
                            </div>
                          </div>

                          <div className="bg-white p-3 sm:p-4">
                            <div className="flex items-start gap-3">
                              <div
                                className={`h-10 w-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${getActionColor(
                                  activity.action,
                                )}`}>
                                {getActionIcon(activity.action)}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-gray-900 leading-relaxed">
                                      <span className="font-medium">
                                        {activity.ownerName}
                                      </span>{" "}
                                      <span className="text-gray-600">
                                        {getActionText(
                                          activity.action,
                                        ).toLowerCase()}
                                      </span>{" "}
                                      {activity.action !== "delete" && (
                                        <span className="text-gray-600">
                                          accommodation
                                        </span>
                                      )}{" "}
                                      <span className="font-medium text-[#D2138C] break-words">
                                        {activity.accommodationTitle}
                                      </span>
                                    </p>
                                  </div>

                                  <div className="flex sm:justify-end">
                                    <Badge
                                      variant="outline"
                                      className={`${getActionColor(activity.action)} text-xs whitespace-nowrap`}>
                                      {getActionText(activity.action)}
                                    </Badge>
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    <span
                                      title={formatFullTimestamp(
                                        activity.timestamp,
                                      )}>
                                      {formatTimestamp(activity.timestamp)}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-1">
                                    <Building2 className="h-3 w-3" />
                                    <span>
                                      ID: #{activity.accommodationId ?? "—"}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    <span className="break-words">
                                      {activity.ownerName}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {openGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenGuide(false)}
            aria-label="Close guide"
          />

          {/* Modal */}
          <div className="relative z-10 w-[95%] max-w-2xl rounded-2xl bg-white shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                SDA Activity Log guide
              </h2>

              <button
                type="button"
                onClick={() => setOpenGuide(false)}
                className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 text-sm text-gray-700 space-y-5">
              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  What this log shows
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>All important actions performed by SDA Owners.</li>
                  <li>Accommodation additions, edits, and deletions.</li>
                  <li>System-recorded activity for auditing purposes.</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  reading an activity
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>The icon shows the type of action.</li>
                  <li>The message explains what happened.</li>
                  <li>The user name shows who performed the action.</li>
                  <li>The timestamp shows when it occurred.</li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end border-t px-6 py-4">
              <button
                type="button"
                onClick={() => setOpenGuide(false)}
                className="rounded-lg bg-[#D2138C] px-4 py-2 text-sm text-white hover:bg-[#B01076]">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SDA_Log;
